import React, {useState, useEffect} from "react";
import ProductCard from "./ProductCard";
import Pagination from "./Pagination";
import LoadMoreButton from "./LoadMoreButton";
import {
    getItemsPerPage,
    setupResponsiveGrid,
    calculateTotalPages,
    getItemsPerRow,
    setupResponsiveColumns,
    getRowsPerLoad,
    setupResponsiveRows
} from "../utils/productUtils";

/**
 * ProductGrid component
 * Displays a grid of products with either pagination or lazy loading
 */
const ProductGrid = ({
                         products = [],
                         hasScrolled,
                         setHasScrolled,
                         usePagination = false,
                         currentQuery = '',
                         clientKey,
                         currencyCode,
                         roundingEnabled,
                         isRecoProductsGrid = false,
                     }) => {
    const initialItemsPerRow = typeof window !== 'undefined' ? getItemsPerRow() : 1;
    const [itemsPerRow, setItemsPerRow] = useState(initialItemsPerRow);
    const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage(initialItemsPerRow));
    const [currentPage, setCurrentPage] = useState(1);

    const initialRowsPerLoad = typeof window !== 'undefined' ? getRowsPerLoad() : 2;
    const [rowsPerLoad, setRowsPerLoad] = useState(initialRowsPerLoad);
    const lazyBatchSize = Math.max(1, (itemsPerRow || 1) * (rowsPerLoad || 2));

    const [visibleProducts, setVisibleProducts] = useState(
        usePagination
            ? []
            : (Array.isArray(products) ? products.slice(0, lazyBatchSize) : [])
    );

    useEffect(() => {
        setCurrentPage(1);
        setVisibleProducts(
            usePagination
                ? []
                : (Array.isArray(products) ? products.slice(0, lazyBatchSize) : [])
        );
    }, [products, usePagination, lazyBatchSize]);

    useEffect(() => {
        return setupResponsiveGrid(() => setItemsPerPage(getItemsPerPage(itemsPerRow)));
    }, [itemsPerRow]);

    useEffect(() => {
        return setupResponsiveColumns(setItemsPerRow);
    }, []);

    useEffect(() => {
        return setupResponsiveRows(setRowsPerLoad);
    }, []);

    useEffect(() => {
        if (usePagination && Array.isArray(products)) {
            const startIndex = (currentPage - 1) * itemsPerPage;
            setVisibleProducts(products.slice(startIndex, startIndex + itemsPerPage));
        } else if (usePagination) {
            setVisibleProducts([]);
        }
    }, [currentPage, itemsPerPage, products, usePagination]);

    const totalPages = Array.isArray(products) ? calculateTotalPages(products, itemsPerPage) : 0;

    const goToPage = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({top: 0, behavior: "smooth"});
        }
    };

    const handleLazyLoad = () => {
        setVisibleProducts((prev) => [
            ...prev,
            ...(Array.isArray(products) ? products.slice(prev.length, prev.length + lazyBatchSize) : []),
        ]);
    };

    useEffect(() => {
        if (hasScrolled) {
            handleLazyLoad();
        }
        setHasScrolled(false);
    }, [hasScrolled]);

    return (
        <div className={`w-full ${!isRecoProductsGrid ? 'min-h-screen' : ''}`}>
            <div className={`grid ${({
                1: 'grid-cols-1',
                2: 'grid-cols-2',
                3: 'grid-cols-3',
                4: 'grid-cols-4',
                5: 'grid-cols-5',
                6: 'grid-cols-6'
            }[itemsPerRow] || 'grid-cols-1')} gap-3 w-full`}>
                {visibleProducts.map((product, index) => (
                    <ProductCard key={index} product={product} index={index} query={currentQuery}
                                 clientKey={clientKey} currencyCode={currencyCode} roundingEnabled={roundingEnabled}/>
                ))}
            </div>

            {usePagination ? (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    goToPage={goToPage}
                />
            ) : (
                <LoadMoreButton
                    onClick={handleLazyLoad}
                    isVisible={false}
                />
            )}
        </div>
    );
};

export default ProductGrid;
